/**
 * Pure rules for which print file a product colour may be saved with.
 *
 * WHY THIS EXISTS: the colour-selection designer makes a colour's on-screen thumbnail and its
 * print file through two independent Design Huddle export chains. Several paths seed a colour's
 * print tracking with another colour's job id + print URL (a new colour copies the locked colour's
 * so its PNG "carries over"; edit-load copies whatever the DB had). When the colour's artwork later
 * changed through a path that only refreshed the thumbnail, the copied print URL survived and was
 * saved — the storefront showed one colourway and production printed another (e.g. a white logo
 * for black garments shipped with the black-ink file). See
 * specs/investigations/2026-10-08-dh-alternate-colour-prints-default-art.md.
 *
 * The rule enforced here: a colour whose artwork differs from the locked colour's must carry a
 * print file exported from its own design, never one borrowed from another colour.
 *
 * No Angular dependencies — the component computes the inputs, these functions decide.
 */

/** A colour/side's print-export tracking: the DH job and the uploaded print file it produced. */
export interface PrintExport {
  url: string;
  jobId: string;
}

/**
 * A comparable key for the artwork a side prints: every `url` under `customizations.classes`
 * (any class name, sorted, case-insensitive) plus the DH template when known. Placement is left
 * out on purpose — it drifts between colours of the same design, and a moved/resized logo is
 * already an editor edit (variantSource 'alternateDesign'), which counts as different anyway.
 */
export function sideArtKey(customizations: any, templateId?: number | string | null): string {
  const classes = customizations?.classes;
  const urls: string[] = [];
  if (classes && typeof classes === 'object') {
    Object.keys(classes).forEach(name => {
      const url = classes[name]?.url;
      if (typeof url === 'string' && url.trim()) {
        urls.push(url.trim().toLowerCase());
      }
    });
  }
  urls.sort();
  const template = templateId !== null && templateId !== undefined && String(templateId) !== '' && String(templateId) !== '0'
    ? String(templateId)
    : '';
  return `${urls.join('|')}#${template}`;
}

/**
 * True when this colour's side prints different art from the locked colour's same side. A colour
 * the user edited in the Design Huddle editor always counts as different (text-only edits are not
 * visible in the logo URLs). An unknown key on either side is not "different".
 */
export function artDiffersFromLocked(variantSource: string | null | undefined, key: string, lockedKey: string): boolean {
  if (variantSource === 'alternateDesign') {
    return true;
  }
  if (!key || !lockedKey || key.startsWith('#') || lockedKey.startsWith('#')) {
    return false;
  }
  return key !== lockedKey;
}

function borrowed(own: PrintExport, others: PrintExport[]): boolean {
  return others.some(o =>
    (!!own.url && !!o?.url && own.url === o.url) ||
    (!!own.jobId && !!o?.jobId && own.jobId === o.jobId));
}

/**
 * True when a colour with its own artwork must be exported before saving: it has no print file of
 * its own, or the one it holds was borrowed from another colour (same URL or same DH job).
 */
export function needsOwnPrintExport(artDiffers: boolean, own: PrintExport, others: PrintExport[]): boolean {
  if (!artDiffers) {
    return false;
  }
  return !own.url || borrowed(own, others);
}

/**
 * The print file to send for a side. Same art: today's behaviour (own URL, else the owner's URL
 * when this side shares the owner's DH job). Different art: only the colour's own, unborrowed URL —
 * otherwise an error, never another colour's file.
 */
export function resolveSideDownloadUrl(
  artDiffers: boolean,
  own: PrintExport,
  owner: PrintExport,
  others: PrintExport[]
): { url: string; error?: 'missing' | 'inherited' } {
  if (!artDiffers) {
    if (own.url) {
      return { url: own.url };
    }
    if (own.jobId && owner?.jobId === own.jobId) {
      return { url: owner?.url || '' };
    }
    return { url: '' };
  }
  if (!own.url) {
    return { url: '', error: 'missing' };
  }
  if (borrowed(own, others)) {
    return { url: '', error: 'inherited' };
  }
  return { url: own.url };
}

/**
 * Whether a non-locked colour is saved as its own design (Case 2, DesignId=false). A colour the
 * user edited always is. A colour whose project id merely differs from the locked colour's — every
 * colour gets a seeded preview project — only is when its artwork actually differs.
 */
export function shouldForceAlternateRouting(isLocked: boolean, projectIdChanged: boolean, artDiffers: boolean): boolean {
  return !isLocked && projectIdChanged && artDiffers;
}
